import { cn } from "@bcc/lib/utils";
import type { ShippingCarrier } from "@client-shared/lib/constants";
import { isShippingCarrier } from "@client-shared/lib/constants";

interface CarrierIconProps {
  readonly carrier?: string;
  readonly className?: string;
  readonly testId?: string;
}

interface CarrierConfig {
  colors: string;
  label?: string;
}

/**
 * Every supported carrier must have an entry — typing the map against ShippingCarrier
 * makes adding a carrier without its badge styling a compile error rather than a
 * silently unstyled (or missing) icon.
 */
const CARRIER_CONFIG: Record<ShippingCarrier, CarrierConfig> = {
  USPS: { colors: "bg-blue-600 text-white" },
  UPS: { colors: "bg-amber-700 text-white" },
  FedEx: { colors: "bg-purple-600 text-white" },
  DHL: { colors: "bg-yellow-500 text-black" },
  DPD: { colors: "bg-red-600 text-white" },
  RoyalMail: { colors: "bg-red-700 text-white", label: "RM" },
  // Brand blue is used for the border rather than the label — blue-on-orange text
  // falls below readable contrast at badge size.
  PostNL: { colors: "bg-carrier-postnl text-white ring-1 ring-inset ring-carrier-postnl-accent" },
  "Australia Post": { colors: "bg-red-500 text-white", label: "AusPost" },
  Shippit: { colors: "bg-indigo-600 text-white" },
  StarTrack: { colors: "bg-orange-600 text-white" },
  "DHL PAKET": { colors: "bg-yellow-500 text-black", label: "DHL Paket" },
};

export function CarrierIcon({ carrier, className, testId }: CarrierIconProps) {
  if (!carrier || !isShippingCarrier(carrier)) return null;

  const { colors, label } = CARRIER_CONFIG[carrier];

  return (
    <span
      data-testid={testId}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none",
        colors,
        className
      )}
    >
      {label ?? carrier}
    </span>
  );
}
